import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Checkbox } from '../components/ui/checkbox';
import { Tabs, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import { Search, UserPlus, Edit2, Trash2, Phone, Mail, Users, Clock, Shield, ArrowUpDown, Camera, GraduationCap, CheckCircle2, X as XIcon, Plus, Upload, Loader2 } from 'lucide-react';
import logger from '../utils/logger';
import { API_BASE } from '../utils/apiBase';

const SERVICE_LABELS = { day_program: 'Explore Program', in_home: 'IHCS', employment: 'Employment', private_pay: 'Private Pay' };
const SERVICE_BADGE_STYLES = {
  day_program: 'bg-[#EDE7F6] text-[#6B3FA0] border border-[#9B7ED8]',
  in_home: 'bg-[#E8F5E9] text-[#1B5E20] border border-[#81C784]',
  employment: 'bg-[#FFF3E0] text-[#E65100] border border-[#FFB74D]',
  private_pay: 'bg-[#E1F5FE] text-[#01579B] border border-[#4FC3F7]',
};

const ROLE_STYLES = {
  administrator: 'bg-[#2D1B4E] text-white',
  supervisor: 'bg-[#6B3FA0] text-white',
  scheduler: 'bg-[#9B7ED8] text-white',
  staff: 'bg-[#F0EDF6] text-[#5A4D6E]',
  board_director: 'bg-[#FFB74D] text-[#5D2D00]',
  hr: 'bg-[#FCE4EC] text-[#AD1457]',
};
const ROLE_LABELS = { administrator: 'Admin', supervisor: 'Supervisor', scheduler: 'Scheduler', staff: 'Staff', board_director: 'Director', hr: 'HR' };
const AVATAR_COLORS = ['#6B3FA0', '#9B7ED8', '#E07A5F', '#2E7D32', '#E65100', '#1565C0', '#AD1457', '#00695C'];
const DAY_KEYS = [['sun','S'],['mon','M'],['tue','T'],['wed','W'],['thu','T'],['fri','F'],['sat','S']];

function getAvatarColor(name) {
  const hash = (name || '').split('').reduce((acc, c) => acc + c.charCodeAt(0), 0);
  return AVATAR_COLORS[hash % AVATAR_COLORS.length];
}

function StaffCard({ emp, onEdit, onDelete, onToggleService, onChangeRole, currentUserRole, positionTitle, apiBase, onOpenTraining }) {
  const role = emp.role || 'staff';
  const isInactive = emp.is_active === false;
  const availability = emp.availability || {};
  const availDays = DAY_KEYS.filter(([k]) => availability[k]).length;
  const trainingDone = emp.training_hours_completed || 0;
  const trainingReq = emp.training_required_hours || 24;
  const trainingPct = Math.min(100, Math.round((trainingDone / (trainingReq || 1)) * 100));
  const trainingComplete = emp.training_complete;
  const photoUrl = emp.has_photo ? `${apiBase}/employees/${emp.id}/photo` : null;

  return (
    <Card className={`border shadow-sm transition-all duration-200 hover:shadow-md hover:-translate-y-0.5 ${
      isInactive ? 'bg-gray-50 border-gray-200 opacity-60' : 'bg-white border-[#DED8EB]'
    }`} data-testid={`staff-card-${emp.id}`}>
      <CardContent className="p-4">
        <div className="flex items-start gap-3">
          {photoUrl ? (
            <img src={photoUrl} alt={emp.name}
              className="w-11 h-11 rounded-xl object-cover shrink-0 shadow-sm border border-[#DED8EB]"
              data-testid={`staff-photo-${emp.id}`} />
          ) : (
            <div className="w-11 h-11 rounded-xl flex items-center justify-center text-white text-base font-bold shrink-0 shadow-sm"
              style={{ backgroundColor: getAvatarColor(emp.name) }}>
              {emp.name?.charAt(0) || '?'}
            </div>
          )}
          <div className="flex-1 min-w-0">
            {/* Header row */}
            <div className="flex items-center justify-between mb-1">
              <div className="flex items-center gap-2 min-w-0">
                <h3 className="text-sm font-bold text-[#2D1B4E] truncate">{emp.name}</h3>
                <Badge className={`text-[9px] shrink-0 ${ROLE_STYLES[role] || ROLE_STYLES.staff}`}>
                  {ROLE_LABELS[role] || 'Staff'}
                </Badge>
                {isInactive && <span className="text-[9px] px-1.5 py-0.5 rounded bg-red-100 text-red-600 font-medium">Inactive</span>}
                {emp.hide_from_scheduling && <span className="text-[9px] px-1.5 py-0.5 rounded bg-[#EDE7F6] text-[#6B3FA0] font-medium border border-[#9B7ED8]" title="Hidden from scheduling pickers">Not Schedulable</span>}
              </div>
              <div className="flex items-center gap-0.5 shrink-0">
                <Button variant="ghost" size="icon" onClick={() => onEdit(emp)} className="h-7 w-7 text-[#8E85A0] hover:text-[#6B3FA0] hover:bg-[#F0EDF6]" data-testid={`edit-staff-${emp.id}`}>
                  <Edit2 className="h-3.5 w-3.5" />
                </Button>
                {currentUserRole === 'administrator' && (
                  <Button variant="ghost" size="icon" onClick={() => onDelete(emp.id)} className="h-7 w-7 text-[#8E85A0] hover:text-[#D9534F] hover:bg-red-50" data-testid={`delete-staff-${emp.id}`}>
                    <Trash2 className="h-3.5 w-3.5" />
                  </Button>
                )}
              </div>
            </div>

            {/* Contact */}
            <div className="flex items-center gap-3 text-xs text-[#8E85A0] mb-2">
              <span className="flex items-center gap-1 truncate"><Mail className="h-3 w-3 shrink-0" />{emp.email}</span>
              {emp.phone && <span className="flex items-center gap-1 shrink-0"><Phone className="h-3 w-3" />{emp.phone}</span>}
            </div>
            {positionTitle && (
              <div className="text-[10px] text-[#6B3FA0] font-semibold mb-2" data-testid={`position-label-${emp.id}`}>
                <Users className="h-3 w-3 inline mr-1" />{positionTitle}
              </div>
            )}

            {/* Hours cap + availability */}
            <div className="flex items-center gap-3 mb-2.5">
              <div className="flex items-center gap-1.5 text-xs text-[#5A4D6E]">
                <Clock className="h-3 w-3 text-[#9B7ED8]" />
                <span style={{ fontFamily: 'IBM Plex Mono, monospace' }} className="font-medium">{emp.max_weekly_hours || 40}h/wk</span>
              </div>
              <div className="flex gap-0.5">
                {DAY_KEYS.map(([key, lbl]) => (
                  <span key={key} className={`w-5 h-5 rounded-md text-[9px] font-bold flex items-center justify-center transition-colors ${
                    availability[key] ? 'bg-[#6B3FA0] text-white' : 'bg-[#F0EDF6] text-[#DED8EB]'
                  }`}>{lbl}</span>
                ))}
                <span className="text-[10px] text-[#8E85A0] ml-1">{availDays}d</span>
              </div>
            </div>

            {/* Training progress */}
            <div className="mb-2" data-testid={`training-progress-${emp.id}`}>
              <div className="flex items-center justify-between mb-1">
                <span className="text-[10px] text-[#8E85A0] flex items-center gap-1">
                  <GraduationCap className="h-3 w-3" />Training
                  {trainingComplete && <CheckCircle2 className="h-3 w-3 text-[#2E7D32]" />}
                </span>
                <button onClick={() => onOpenTraining(emp)}
                  className={`text-[10px] font-mono font-semibold hover:underline ${trainingComplete ? 'text-[#2E7D32]' : trainingPct >= 50 ? 'text-[#E65100]' : 'text-[#D9534F]'}`}
                  data-testid={`training-btn-${emp.id}`}>
                  {trainingDone}/{trainingReq}h
                </button>
              </div>
              <div className="w-full h-1.5 rounded-full bg-[#F0EDF6] overflow-hidden">
                <div className={`h-full transition-all ${trainingComplete ? 'bg-[#2E7D32]' : trainingPct >= 50 ? 'bg-[#E65100]' : 'bg-[#D9534F]'}`}
                  style={{ width: `${trainingPct}%` }} />
              </div>
            </div>

            {/* Service lines */}
            <div className="flex flex-wrap gap-1.5 mb-1.5">
              {Object.entries(SERVICE_LABELS).map(([key, label]) => {
                const active = (emp.service_lines || []).includes(key);
                return (
                  <button key={key} onClick={() => onToggleService(emp, key)}
                    className={`text-[10px] font-semibold px-2.5 py-1 rounded-lg border transition-all duration-200 ${
                      active ? SERVICE_BADGE_STYLES[key] : 'bg-white text-[#C4BDD2] border-[#E8E4EE] hover:border-[#9B7ED8] hover:text-[#5A4D6E]'
                    }`} data-testid={`toggle-${key}-${emp.id}`}>
                    {label}
                  </button>
                );
              })}
            </div>

            {/* Role quick-toggle (admins can promote/demote anyone, including to/from admin) */}
            {currentUserRole === 'administrator' && (
              <div className="flex items-center gap-1 pt-1.5 border-t border-[#F0EDF6] flex-wrap">
                <Shield className="h-3 w-3 text-[#8E85A0] mr-0.5 shrink-0" />
                {['staff', 'scheduler', 'supervisor', 'administrator', 'board_director', 'hr'].map(r => (
                  <button key={r} onClick={() => onChangeRole(emp, r)}
                    className={`text-[9px] font-semibold px-1.5 py-0.5 rounded-md border transition-all whitespace-nowrap ${
                      role === r ? (ROLE_STYLES[r] || '') + ' border-transparent' : 'bg-white text-[#8E85A0] border-[#E8E4EE] hover:border-[#9B7ED8]'
                    }`} data-testid={`role-${r}-${emp.id}`}>
                    {ROLE_LABELS[r]}
                  </button>
                ))}
              </div>
            )}

            {/* Scope for supervisors/schedulers */}
            {(role === 'supervisor' || role === 'scheduler') && (
              <div className="mt-1 text-[10px] text-[#8E85A0]">
                Scope: {(emp.service_scope || []).length === 0 ? 'All Services' : (emp.service_scope || []).map(s => ({ day_program: 'CPS', employment: 'Emp', in_home: 'IHCS', private_pay: 'PP' }[s] || s)).join(', ')}
              </div>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

export default function EmployeeManagement() {
  const { api, user } = useAuth();
  const apiBase = API_BASE + '/api';
  const [employees, setEmployees] = useState([]);
  const [positions, setPositions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [formData, setFormData] = useState({ name: '', email: '', password: '', phone: '', service_lines: [], service_scope: [], role: 'staff', max_weekly_hours: 40, availability: {}, position_id: '', hide_from_scheduling: false });
  const [saving, setSaving] = useState(false);
  const [search, setSearch] = useState('');
  const [filterTab, setFilterTab] = useState('all');
  const [sortBy, setSortBy] = useState('name_asc');
  const [trainingDialog, setTrainingDialog] = useState(null);
  const [photoUploading, setPhotoUploading] = useState(false);
  const currentUserRole = user?.role || 'staff';

  const openTraining = (emp) => setTrainingDialog(emp);

  const uploadPhoto = async (empId, file) => {
    if (!file) return;
    if (!file.type.startsWith('image/')) { toast.error('Photo must be JPG, PNG or WebP'); return; }
    if (file.size > 3 * 1024 * 1024) { toast.error('Photo exceeds 3 MB'); return; }
    setPhotoUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      await api.post(`/employees/${empId}/photo`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      toast.success('Photo updated');
      fetchEmployees();
    } catch (e) { toast.error('Upload failed'); logger.error(e); }
    setPhotoUploading(false);
  };

  const removePhoto = async (empId) => {
    if (!window.confirm('Remove this photo?')) return;
    try { await api.delete(`/employees/${empId}/photo`); toast.success('Photo removed'); fetchEmployees(); }
    catch { toast.error('Failed'); }
  };

  const fetchEmployees = useCallback(async () => {
    try { const { data } = await api.get('/employees?include_hidden=true'); setEmployees(data); } catch (e) { logger.error(e); }
    setLoading(false);
  }, [api]);

  const fetchPositions = useCallback(async () => {
    try { const { data } = await api.get('/staffing/positions'); setPositions(data); } catch (e) { logger.error(e); }
  }, [api]);

  useEffect(() => { fetchEmployees(); fetchPositions(); }, [fetchEmployees, fetchPositions]);

  const openCreate = () => {
    setEditing(null);
    setFormData({ name: '', email: '', password: '', phone: '', service_lines: [], service_scope: [], role: 'staff', max_weekly_hours: 40, availability: { sun: false, mon: true, tue: true, wed: true, thu: true, fri: true, sat: false }, position_id: '', hide_from_scheduling: false });
    setDialogOpen(true);
  };

  const openEdit = (emp) => {
    setEditing(emp);
    setFormData({ name: emp.name, email: emp.email, password: '', phone: emp.phone || '', service_lines: emp.service_lines || [], service_scope: emp.service_scope || [], role: emp.role || 'staff', max_weekly_hours: emp.max_weekly_hours || 40, availability: emp.availability || { sun: false, mon: true, tue: true, wed: true, thu: true, fri: true, sat: false }, position_id: emp.position_id || '', hide_from_scheduling: !!emp.hide_from_scheduling });
    setDialogOpen(true);
  };

  const toggleFormServiceLine = (sl) => { setFormData(p => ({ ...p, service_lines: p.service_lines.includes(sl) ? p.service_lines.filter(s => s !== sl) : [...p.service_lines, sl] })); };

  const handleSave = async () => {
    setSaving(true);
    try {
      if (editing) {
        const payload = { name: formData.name, phone: formData.phone, service_lines: formData.service_lines, service_scope: formData.service_scope, role: formData.role, max_weekly_hours: formData.max_weekly_hours, availability: formData.availability, position_id: formData.position_id || '', hide_from_scheduling: !!formData.hide_from_scheduling };
        if (formData.email !== editing.email) payload.email = formData.email;
        if (formData.password) payload.password = formData.password;
        await api.put(`/employees/${editing.id}`, payload);
        toast.success('Staff updated');
      } else {
        await api.post('/employees', formData);
        toast.success('Staff created');
      }
      setDialogOpen(false); fetchEmployees(); fetchPositions();
    } catch (e) { toast.error(e.response?.data?.detail || 'Error saving'); }
    setSaving(false);
  };

  const handleDelete = async (empId) => {
    if (!window.confirm('Remove this staff member?')) return;
    try { await api.delete(`/employees/${empId}`); toast.success('Staff removed'); fetchEmployees(); } catch (e) { logger.error(e); }
  };

  const handleToggleService = async (emp, serviceLine) => {
    const current = emp.service_lines || [];
    const updated = current.includes(serviceLine) ? current.filter(s => s !== serviceLine) : [...current, serviceLine];
    try { await api.put(`/employees/${emp.id}`, { service_lines: updated }); fetchEmployees(); } catch (e) { logger.error(e); }
  };

  const handleChangeRole = async (emp, newRole) => {
    if (emp.role === newRole) return;
    // Confirm when flipping to/from Director since the side-effects are big
    if (newRole === 'board_director') {
      if (!window.confirm(`Make ${emp.name} a Board Director?\n\nThey will:\n  • Lose access to scheduling, finance, and admin features\n  • See only the Board Portal on login\n  • Be hidden from scheduling pickers\n  • Get board portal access automatically`)) return;
    } else if (emp.role === 'board_director') {
      if (!window.confirm(`Change ${emp.name} from Board Director to ${ROLE_LABELS[newRole]}?\n\nThey will regain access to staff/operational features and become schedulable again.`)) return;
    }
    try {
      await api.put(`/employees/${emp.id}/role?role=${newRole}`);
      toast.success(`${emp.name} is now ${ROLE_LABELS[newRole]}`);
      fetchEmployees();
    } catch (e) {
      logger.error(e);
      toast.error(e.response?.data?.detail || 'Failed to change role');
    }
  };

  const filtered = useMemo(() => {
    const q = search.toLowerCase();
    let result = employees.filter(emp => {
      const matchesSearch = !q || emp.name.toLowerCase().includes(q) || emp.email.toLowerCase().includes(q);
      const matchesFilter = filterTab === 'all' || (emp.service_lines || []).includes(filterTab);
      return matchesSearch && matchesFilter;
    });
    const roleOrder = { administrator: 0, supervisor: 1, scheduler: 2, staff: 3, board_director: 4 };
    result.sort((a, b) => {
      switch (sortBy) {
        case 'name_asc': return (a.name || '').localeCompare(b.name || '');
        case 'name_desc': return (b.name || '').localeCompare(a.name || '');
        case 'role': return (roleOrder[a.role] ?? 3) - (roleOrder[b.role] ?? 3) || (a.name || '').localeCompare(b.name || '');
        case 'hours': return (b.max_weekly_hours || 40) - (a.max_weekly_hours || 40);
        default: return (a.name || '').localeCompare(b.name || '');
      }
    });
    return result;
  }, [employees, search, filterTab, sortBy]);

  const stats = useMemo(() => {
    const counts = { all: employees.length, day_program: 0, in_home: 0, employment: 0, private_pay: 0 };
    const roles = { administrator: 0, supervisor: 0, scheduler: 0, staff: 0 };
    const active = employees.filter(e => e.is_active !== false).length;
    employees.forEach(emp => {
      (emp.service_lines || []).forEach(sl => { counts[sl] = (counts[sl] || 0) + 1; });
      roles[emp.role || 'staff'] = (roles[emp.role || 'staff'] || 0) + 1;
    });
    return { counts, roles, active, inactive: employees.length - active };
  }, [employees]);

  return (
    <div className="p-4 sm:p-8 space-y-5" data-testid="employee-management">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl sm:text-4xl tracking-tight font-extrabold text-[#2D1B4E]" style={{ fontFamily: 'Manrope, sans-serif' }}>Staff</h1>
          <p className="text-xs sm:text-sm text-[#8E85A0] mt-1">{employees.length} team members</p>
        </div>
        <Button onClick={openCreate} className="bg-[#6B3FA0] hover:bg-[#5A2F8E] text-white gap-2 h-10" data-testid="add-employee-btn">
          <UserPlus className="h-4 w-4" /> Add Staff
        </Button>
      </div>

      {/* Stats row */}
      <div className="flex items-center gap-3 flex-wrap">
        <div className="flex items-center gap-4 px-4 py-2.5 rounded-xl bg-white border border-[#DED8EB] shadow-sm">
          <div className="text-center">
            <p className="text-xl font-bold text-[#2D1B4E]" style={{ fontFamily: 'Manrope, sans-serif' }}>{stats.active}</p>
            <p className="text-[10px] text-[#8E85A0] uppercase tracking-wider font-semibold">Active</p>
          </div>
          {stats.inactive > 0 && (
            <div className="text-center border-l border-[#DED8EB] pl-4">
              <p className="text-xl font-bold text-red-400">{stats.inactive}</p>
              <p className="text-[10px] text-[#8E85A0] uppercase tracking-wider font-semibold">Inactive</p>
            </div>
          )}
        </div>
        <div className="flex items-center gap-2 px-3 py-2 rounded-xl bg-white border border-[#DED8EB] shadow-sm">
          {[['administrator', '#2D1B4E'], ['supervisor', '#6B3FA0'], ['scheduler', '#9B7ED8'], ['staff', '#DED8EB']].map(([role, color]) => (
            stats.roles[role] > 0 && (
              <div key={role} className="flex items-center gap-1.5">
                <div className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: color }} />
                <span className="text-xs text-[#5A4D6E] font-medium">{stats.roles[role]} {ROLE_LABELS[role]}</span>
              </div>
            )
          ))}
        </div>
      </div>

      {/* Filter tabs + search */}
      <div className="flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-4">
        <Tabs value={filterTab} onValueChange={setFilterTab} className="flex-1 min-w-0">
          <TabsList className="bg-[#F0EDF6] border border-[#DED8EB] w-full sm:w-auto overflow-x-auto flex-nowrap justify-start" data-testid="staff-filter-tabs">
            <TabsTrigger value="all" className="data-[state=active]:bg-white gap-1 shrink-0">
              All <Badge className="text-[10px] bg-[#DED8EB] text-[#5A4D6E] h-5 min-w-5 rounded-full">{stats.counts.all}</Badge>
            </TabsTrigger>
            <TabsTrigger value="day_program" className="data-[state=active]:bg-white gap-1 shrink-0">
              Explore <Badge className="text-[10px] bg-[#EDE7F6] text-[#6B3FA0] h-5 min-w-5 rounded-full">{stats.counts.day_program}</Badge>
            </TabsTrigger>
            <TabsTrigger value="employment" className="data-[state=active]:bg-white gap-1 shrink-0">
              Emp <Badge className="text-[10px] bg-[#FFF3E0] text-[#E65100] h-5 min-w-5 rounded-full">{stats.counts.employment}</Badge>
            </TabsTrigger>
            <TabsTrigger value="in_home" className="data-[state=active]:bg-white gap-1 shrink-0">
              IHCS <Badge className="text-[10px] bg-[#E8F5E9] text-[#1B5E20] h-5 min-w-5 rounded-full">{stats.counts.in_home}</Badge>
            </TabsTrigger>
            <TabsTrigger value="private_pay" className="data-[state=active]:bg-white gap-1 shrink-0">
              Private Pay <Badge className="text-[10px] bg-[#E1F5FE] text-[#01579B] h-5 min-w-5 rounded-full">{stats.counts.private_pay}</Badge>
            </TabsTrigger>
          </TabsList>
        </Tabs>
        <div className="flex items-center gap-2">
          <div className="relative flex-1 sm:w-56">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[#8E85A0]" />
            <Input placeholder="Search staff..." value={search} onChange={(e) => setSearch(e.target.value)}
              className="pl-9 border-[#DED8EB] bg-white h-10 text-sm" data-testid="employee-search" />
          </div>
          <Select value={sortBy} onValueChange={setSortBy}>
            <SelectTrigger className="w-28 sm:w-40 border-[#DED8EB] bg-white h-10 text-sm shrink-0" data-testid="staff-sort">
              <ArrowUpDown className="h-3.5 w-3.5 mr-1.5 text-[#8E85A0]" /><SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="name_asc">Name A-Z</SelectItem>
              <SelectItem value="name_desc">Name Z-A</SelectItem>
              <SelectItem value="role">By Role</SelectItem>
              <SelectItem value="hours">By Hours Cap</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Staff grid */}
      {loading ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
          {[1,2,3,4,5,6].map(i => <div key={i} className="h-36 bg-[#DED8EB] rounded-xl animate-pulse" />)}
        </div>
      ) : filtered.length === 0 ? (
        <div className="text-center py-12 text-[#8E85A0]">
          <Users className="h-10 w-10 mx-auto mb-2 opacity-50" />
          <p>{search ? 'No staff match your search' : 'No staff yet'}</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3" data-testid="staff-grid">
          {filtered.map(emp => (
            <StaffCard key={emp.id} emp={emp} onEdit={openEdit} onDelete={handleDelete} onToggleService={handleToggleService} onChangeRole={handleChangeRole} currentUserRole={currentUserRole} positionTitle={positions.find(p => p.id === emp.position_id)?.title} apiBase={apiBase} onOpenTraining={openTraining} />
          ))}
        </div>
      )}

      {/* Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="sm:max-w-md bg-white max-h-[85vh] overflow-y-auto" data-testid="employee-dialog">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold text-[#2D1B4E]" style={{ fontFamily: 'Manrope, sans-serif' }}>{editing ? 'Edit Staff' : 'Add Staff'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            {editing && editing.id && (
              <div className="flex items-center gap-3 p-3 rounded-lg border border-[#DED8EB] bg-[#F7F4EF]">
                {editing.has_photo ? (
                  <img src={`${apiBase}/employees/${editing.id}/photo?t=${Date.now()}`} alt={editing.name}
                    className="w-14 h-14 rounded-xl object-cover border border-[#DED8EB]" />
                ) : (
                  <div className="w-14 h-14 rounded-xl flex items-center justify-center text-white text-lg font-bold shadow-sm"
                    style={{ backgroundColor: getAvatarColor(editing.name) }}>{editing.name?.charAt(0) || '?'}</div>
                )}
                <div className="flex-1">
                  <p className="text-sm font-semibold text-[#2D1B4E]">Profile Photo</p>
                  <p className="text-[10px] text-[#8E85A0]">JPG / PNG / WebP, 3 MB max</p>
                </div>
                <div className="flex gap-1">
                  <label className="cursor-pointer">
                    <input type="file" accept="image/*" className="hidden" onChange={(e) => {
                      const f = e.target.files?.[0];
                      if (f) uploadPhoto(editing.id, f);
                      e.target.value = '';
                    }} data-testid="photo-upload-input" />
                    <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-md border border-[#9B7ED8] text-[#6B3FA0] text-xs font-semibold hover:bg-[#EDE7F6] ${photoUploading ? 'opacity-50' : ''}`}>
                      {photoUploading ? <Loader2 className="h-3 w-3 animate-spin" /> : <Camera className="h-3 w-3" />}
                      {editing.has_photo ? 'Change' : 'Upload'}
                    </span>
                  </label>
                  {editing.has_photo && (
                    <Button size="icon" variant="ghost" onClick={() => removePhoto(editing.id)} className="h-7 w-7 text-[#8E85A0] hover:text-[#D9534F]" data-testid="photo-remove-btn">
                      <XIcon className="h-3.5 w-3.5" />
                    </Button>
                  )}
                </div>
              </div>
            )}
            <div className="space-y-2"><Label className="text-[#2D1B4E] font-medium">Full Name</Label><Input value={formData.name} onChange={(e) => setFormData(p => ({ ...p, name: e.target.value }))} placeholder="First Last" className="border-[#DED8EB]" data-testid="employee-name-input" /></div>
            <div className="space-y-2"><Label className="text-[#2D1B4E] font-medium">Email</Label><Input value={formData.email} onChange={(e) => setFormData(p => ({ ...p, email: e.target.value }))} placeholder="email@tnsprograms.org" type="email" className="border-[#DED8EB]" data-testid="employee-email-input" disabled={editing && currentUserRole !== 'administrator'} /></div>
            <div className="space-y-2">
              <Label className="text-[#2D1B4E] font-medium">{editing ? 'New Password' : 'Password'} {editing && <span className="text-[#8E85A0] font-normal text-xs">(leave blank to keep current)</span>}</Label>
              <Input value={formData.password} onChange={(e) => setFormData(p => ({ ...p, password: e.target.value }))} placeholder={editing ? "Leave blank to keep current" : "Min 8 characters"} type="password" className="border-[#DED8EB]" data-testid="employee-password-input" />
            </div>
            <div className="space-y-2"><Label className="text-[#2D1B4E] font-medium">Phone</Label><Input value={formData.phone} onChange={(e) => setFormData(p => ({ ...p, phone: e.target.value }))} placeholder="(555) 123-4567" className="border-[#DED8EB]" data-testid="employee-phone-input" /></div>
            <div className="space-y-2">
              <Label className="text-[#2D1B4E] font-medium">Role</Label>
              <div className="flex gap-1.5 flex-wrap">
                {(currentUserRole === 'administrator' ? ['staff', 'scheduler', 'supervisor', 'administrator', 'hr'] : ['staff', 'scheduler', 'supervisor']).map(r => (
                  <button key={r} type="button" onClick={() => setFormData(p => ({ ...p, role: r }))}
                    className={`text-xs font-medium px-3 py-1.5 rounded-lg border transition-all ${formData.role === r ? 'bg-[#6B3FA0] text-white border-[#6B3FA0]' : 'bg-white text-[#5A4D6E] border-[#DED8EB] hover:border-[#9B7ED8]'}`} data-testid={`form-role-${r}`}>
                    {{ administrator: 'Admin', supervisor: 'Supervisor', scheduler: 'Scheduler', staff: 'Staff', hr: 'HR' }[r]}
                  </button>
                ))}
              </div>
            </div>
            {(formData.role === 'supervisor' || formData.role === 'scheduler') && (
              <div className="space-y-2">
                <Label className="text-[#2D1B4E] font-medium">Service Scope <span className="text-xs text-[#8E85A0] font-normal">(services they manage)</span></Label>
                {Object.entries(SERVICE_LABELS).map(([key, label]) => (
                  <div key={`scope-${key}`} className="flex items-center gap-2">
                    <Checkbox id={`scope-${key}`} checked={(formData.service_scope || []).includes(key)}
                      onCheckedChange={() => setFormData(p => ({ ...p, service_scope: (p.service_scope || []).includes(key) ? p.service_scope.filter(s => s !== key) : [...(p.service_scope || []), key] }))} data-testid={`scope-${key}`} />
                    <label htmlFor={`scope-${key}`} className="text-sm text-[#5A4D6E] cursor-pointer">{label}</label>
                  </div>
                ))}
              </div>
            )}
            <div className="space-y-2">
              <Label className="text-[#2D1B4E] font-medium">Service Lines <span className="text-xs text-[#8E85A0] font-normal">(services they work in)</span></Label>
              {Object.entries(SERVICE_LABELS).map(([key, label]) => (
                <div key={key} className="flex items-center gap-2">
                  <Checkbox id={`sl-${key}`} checked={formData.service_lines.includes(key)} onCheckedChange={() => toggleFormServiceLine(key)} data-testid={`service-line-${key}`} />
                  <label htmlFor={`sl-${key}`} className="text-sm text-[#5A4D6E] cursor-pointer">{label}</label>
                </div>
              ))}
            </div>
            <div className="space-y-2"><Label className="text-[#2D1B4E] font-medium">Max Weekly Hours</Label><Input type="number" value={formData.max_weekly_hours} onChange={e => setFormData(p => ({ ...p, max_weekly_hours: parseFloat(e.target.value) || 40 }))} className="border-[#DED8EB] w-24" data-testid="employee-max-hours" /></div>
            <div className="space-y-2">
              <Label className="text-[#2D1B4E] font-medium">Position <span className="text-xs text-[#8E85A0] font-normal">(links to Staffing org chart)</span></Label>
              <select value={formData.position_id || ''} onChange={e => setFormData(p => ({ ...p, position_id: e.target.value }))}
                className="w-full border border-[#DED8EB] rounded-md px-3 py-2 text-sm bg-white" data-testid="employee-position-select">
                <option value="">— Unassigned —</option>
                {positions.map(pos => (
                  <option key={pos.id} value={pos.id}>{pos.title}{pos.service_line ? ` · ${SERVICE_LABELS[pos.service_line] || pos.service_line}` : ''}</option>
                ))}
              </select>
            </div>
            <div className="space-y-2">
              <Label className="text-[#2D1B4E] font-medium">Availability</Label>
              <div className="flex gap-1.5">
                {DAY_KEYS.map(([key, lbl]) => (
                  <button key={key} type="button" onClick={() => setFormData(p => ({ ...p, availability: { ...p.availability, [key]: !p.availability?.[key] } }))}
                    className={`w-9 h-9 rounded-lg text-xs font-bold transition-all ${formData.availability?.[key] ? 'bg-[#6B3FA0] text-white shadow-sm' : 'bg-[#F0EDF6] text-[#8E85A0] border border-[#DED8EB]'}`}
                    data-testid={`avail-${key}`}>{lbl}</button>
                ))}
              </div>
            </div>
            <div className="space-y-2 border-t border-[#F0EDF6] pt-3">
              <label className="flex items-start gap-2 cursor-pointer">
                <Checkbox
                  id="hide_from_scheduling"
                  checked={!!formData.hide_from_scheduling}
                  onCheckedChange={(v) => setFormData(p => ({ ...p, hide_from_scheduling: !!v }))}
                  data-testid="employee-hide-from-scheduling"
                />
                <div className="flex-1">
                  <span className="text-sm font-medium text-[#2D1B4E]">Hide from scheduling</span>
                  <p className="text-[11px] text-[#8E85A0] leading-snug">
                    Keep this user out of the scheduling picker, schedule grid, and staff selectors. Use this for board members, contractors, or any login account that should not be schedulable. They'll still appear here in Staff Management for editing.
                  </p>
                </div>
              </label>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)} className="border-[#DED8EB]" data-testid="employee-cancel-btn">Cancel</Button>
            <Button onClick={handleSave} disabled={saving || !formData.name || (!editing && (!formData.email || !formData.password))} className="bg-[#6B3FA0] hover:bg-[#5A2F8E] text-white" data-testid="employee-save-btn">
              {saving ? 'Saving...' : editing ? 'Update' : 'Create'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {trainingDialog && (
        <TrainingDialog employee={trainingDialog} api={api} currentUserRole={currentUserRole} onClose={() => { setTrainingDialog(null); fetchEmployees(); }} />
      )}
    </div>
  );
}

function TrainingDialog({ employee, api, currentUserRole, onClose }) {
  const isAdmin = ['administrator', 'supervisor'].includes(currentUserRole);
  const [data, setData] = useState({ entries: [], total_hours: 0, required_hours: 24, remaining_hours: 24, complete: false });
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState({ title: '', category: 'Orientation', hours: 1, date: new Date().toISOString().slice(0, 10), notes: '' });
  const [saving, setSaving] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [reqHours, setReqHours] = useState(24);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/employees/${employee.id}/training`);
      setData(data);
      setReqHours(data.required_hours);
    } catch (e) { logger.error(e); }
    setLoading(false);
  }, [api, employee.id]);

  useEffect(() => { load(); }, [load]);

  const submit = async () => {
    if (!form.title.trim() || !form.hours) { toast.error('Title and hours required'); return; }
    setSaving(true);
    try {
      if (editingId) {
        await api.put(`/employees/${employee.id}/training/${editingId}`, form);
        toast.success('Entry updated');
      } else {
        await api.post(`/employees/${employee.id}/training`, form);
        toast.success('Entry added');
      }
      setForm({ title: '', category: 'Orientation', hours: 1, date: new Date().toISOString().slice(0, 10), notes: '' });
      setEditingId(null);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
    setSaving(false);
  };

  const startEdit = (e) => {
    setEditingId(e.id);
    setForm({ title: e.title, category: e.category || 'Other', hours: e.hours, date: e.date || '', notes: e.notes || '' });
  };

  const del = async (id) => {
    if (!window.confirm('Delete this training entry?')) return;
    try { await api.delete(`/employees/${employee.id}/training/${id}`); toast.success('Deleted'); load(); }
    catch { toast.error('Failed'); }
  };

  const saveRequired = async () => {
    try { await api.put(`/employees/${employee.id}/training-required`, { hours: parseFloat(reqHours) || 24 }); toast.success('Requirement saved'); load(); }
    catch { toast.error('Failed'); }
  };

  const pct = Math.min(100, Math.round((data.total_hours / (data.required_hours || 1)) * 100));

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="sm:max-w-2xl bg-white max-h-[88vh] overflow-y-auto" data-testid="training-dialog">
        <DialogHeader>
          <DialogTitle className="text-xl font-bold text-[#2D1B4E] flex items-center gap-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
            <GraduationCap className="h-5 w-5 text-[#6B3FA0]" /> {employee.name} — Training
          </DialogTitle>
        </DialogHeader>
        {loading ? <div className="text-center py-6 text-[#8E85A0]">Loading...</div> : (
          <div className="space-y-4">
            <div className={`p-3 rounded-lg border ${data.complete ? 'border-[#81C784] bg-[#E8F5E9]' : 'border-[#FFB74D] bg-[#FFF3E0]'}`}>
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-bold text-[#2D1B4E]">
                  {data.total_hours} / {data.required_hours} hours {data.complete ? '✓ Complete' : `· ${data.remaining_hours}h remaining`}
                </span>
                <div className="flex items-center gap-2">
                  <Input type="number" step="1" value={reqHours} onChange={e => setReqHours(e.target.value)}
                    disabled={!['administrator'].includes(currentUserRole)}
                    className="w-16 h-7 text-xs" data-testid="required-hours-input" />
                  {currentUserRole === 'administrator' && <Button size="sm" variant="outline" onClick={saveRequired} className="h-7 text-xs" data-testid="save-required-hours">Set</Button>}
                </div>
              </div>
              <div className="w-full h-2 rounded-full bg-white/60 overflow-hidden">
                <div className={`h-full ${data.complete ? 'bg-[#2E7D32]' : 'bg-[#E65100]'}`} style={{ width: `${pct}%` }} />
              </div>
            </div>

            {isAdmin && (
              <div className="p-3 rounded-lg border border-[#DED8EB] bg-[#F7F4EF]" data-testid="training-add-form">
                <p className="text-xs font-bold uppercase tracking-wider text-[#6B3FA0] mb-2">{editingId ? 'Edit entry' : 'Log training'}</p>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                  <Input value={form.title} onChange={e => setForm(p => ({ ...p, title: e.target.value }))} placeholder="e.g. CPR Initial Cert" className="border-[#DED8EB]" data-testid="training-title-input" />
                  <select value={form.category} onChange={e => setForm(p => ({ ...p, category: e.target.value }))}
                    className="border border-[#DED8EB] rounded-md px-3 py-2 text-sm" data-testid="training-category-input">
                    {['Orientation', 'CPR / First Aid', 'Medication Admin', 'Crisis Response', 'Compliance', 'Skill / Elective', 'Other'].map(c => <option key={c}>{c}</option>)}
                  </select>
                  <Input type="number" step="0.5" value={form.hours} onChange={e => setForm(p => ({ ...p, hours: parseFloat(e.target.value) || 0 }))} placeholder="Hours" className="border-[#DED8EB]" data-testid="training-hours-input" />
                  <Input type="date" value={form.date} onChange={e => setForm(p => ({ ...p, date: e.target.value }))} className="border-[#DED8EB]" data-testid="training-date-input" />
                  <Input value={form.notes} onChange={e => setForm(p => ({ ...p, notes: e.target.value }))} placeholder="Notes (optional)" className="border-[#DED8EB] md:col-span-2" data-testid="training-notes-input" />
                </div>
                <div className="flex gap-2 mt-2">
                  <Button onClick={submit} disabled={saving} className="bg-[#6B3FA0] hover:bg-[#5A2F8E] text-white h-8 text-xs gap-1" data-testid="training-save-btn">
                    {saving ? <Loader2 className="h-3 w-3 animate-spin" /> : <Plus className="h-3 w-3" />}
                    {editingId ? 'Update' : 'Add'}
                  </Button>
                  {editingId && <Button variant="outline" onClick={() => { setEditingId(null); setForm({ title: '', category: 'Orientation', hours: 1, date: new Date().toISOString().slice(0, 10), notes: '' }); }} className="h-8 text-xs">Cancel</Button>}
                </div>
              </div>
            )}

            <div className="space-y-1" data-testid="training-log">
              {data.entries.length === 0 ? <p className="text-sm text-[#8E85A0] text-center py-4">No training logged yet.</p> :
                data.entries.map(e => (
                  <div key={e.id} className="flex items-center justify-between p-2 rounded-md border border-[#F0EDF6] hover:bg-[#F7F4EF]" data-testid={`training-row-${e.id}`}>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-semibold text-[#2D1B4E]">{e.title}</p>
                      <p className="text-[10px] text-[#8E85A0]">{e.date} · {e.category} · {e.hours}h{e.notes ? ` · ${e.notes}` : ''}</p>
                    </div>
                    {isAdmin && (
                      <div className="flex gap-1">
                        <Button size="icon" variant="ghost" onClick={() => startEdit(e)} className="h-6 w-6 text-[#8E85A0] hover:text-[#6B3FA0]"><Edit2 className="h-3 w-3" /></Button>
                        <Button size="icon" variant="ghost" onClick={() => del(e.id)} className="h-6 w-6 text-[#8E85A0] hover:text-[#D9534F]"><Trash2 className="h-3 w-3" /></Button>
                      </div>
                    )}
                  </div>
                ))}
            </div>
          </div>
        )}
        <DialogFooter><Button variant="outline" onClick={onClose}>Close</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
